import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Description,
    Dropdown,
    Label,
    Skeleton,
    Table,
} from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { VatDrillTarget, VatRow } from '../../vatMeta';

type VatBreakdownTableProps = {
    /** Section heading, e.g. "Output VAT". */
    title: string;
    /** Descriptive grid label, used as the table's `aria-label`. */
    ariaLabel: string;
    rows: VatRow[];
    totalText: string | null;
    /** The accounts behind the total — the multi-account drill case. */
    totalDrillTargets: VatDrillTarget[];
    /**
     * The report is still in flight. The table keeps its real column headers and
     * skeletons only the rows — an empty list while loading means "not known
     * yet", never "no activity".
     */
    isLoading?: boolean;
    /** Shown when the side has no VAT activity in the period. */
    emptyText: string;
    onViewLedger: (accountId: string) => void;
    /** Literal testid prefix, e.g. `vat-output` / `vat-input`. */
    testIdPrefix: string;
};

const DRILL_CLASS_NAME =
    'ml-1 inline-flex h-auto min-h-0 cursor-[var(--cursor-interactive,pointer)] items-center gap-1 whitespace-nowrap p-0 text-xs font-medium text-accent opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100';

const SKELETON_ROW_COUNT = 3;

/**
 * One VAT side rendered with the statements' table conventions: account code
 * before name, right-aligned tabular figures, a hover/focus "View in ledger"
 * drill per account, and a heavy total row. Flat (accounts don't nest), so no
 * indentation. An empty side is a legitimate result, not an empty state.
 *
 * The OSS `Table` compound rather than Pro's `DataGrid`: this table ends in a
 * **total row**, and rows carry per-row styling (the `group` that reveals the
 * drill, the heavy total). `DataGrid` has no footer slot and exposes no per-row
 * className, so it cannot express either. `Table.Row` takes both.
 *
 * Negative figures take the danger token, as they do on the statements — a
 * line's amount is a directional movement and can be negative when an account is
 * tagged against the way its balance moves.
 */
export const VatBreakdownTable: React.FC<VatBreakdownTableProps> = ({
    title,
    ariaLabel,
    rows,
    totalText,
    totalDrillTargets,
    emptyText,
    isLoading = false,
    onViewLedger,
    testIdPrefix,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <div {...getNodeTestIdProp(`${testIdPrefix}-table`)}>
                <Table>
                    <Table.ScrollContainer>
                        <Table.Content
                            aria-label={ariaLabel}
                            className="text-sm"
                        >
                            <Table.Header>
                                <Table.Column id="account" isRowHeader>
                                    {t('vat.breakdown.account')}
                                </Table.Column>
                                <Table.Column id="amount">
                                    {t('vat.breakdown.amount')}
                                </Table.Column>
                            </Table.Header>
                            <Table.Body>
                                {isLoading
                                    ? Array.from(
                                          { length: SKELETON_ROW_COUNT },
                                          (_, index) => (
                                              <Table.Row
                                                  id={`${testIdPrefix}-skeleton-${index}`}
                                                  key={`${testIdPrefix}-skeleton-${index}`}
                                              >
                                                  <Table.Cell>
                                                      <Skeleton className="h-4 w-full rounded" />
                                                  </Table.Cell>
                                                  <Table.Cell>
                                                      <Skeleton className="ml-auto h-4 w-24 rounded" />
                                                  </Table.Cell>
                                              </Table.Row>
                                          ),
                                      )
                                    : rows.length === 0
                                      ? [
                                            <Table.Row
                                                id={`${testIdPrefix}-empty-row`}
                                                key={`${testIdPrefix}-empty-row`}
                                            >
                                                <Table.Cell>
                                                    <span
                                                        className="text-sm text-muted"
                                                        {...getNodeTestIdProp(
                                                            `${testIdPrefix}-empty`,
                                                        )}
                                                    >
                                                        {emptyText}
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell>{''}</Table.Cell>
                                            </Table.Row>,
                                        ]
                                      : rows.map((row) => (
                                            <Table.Row
                                                className="group"
                                                id={row.accountId}
                                                key={row.accountId}
                                            >
                                                <Table.Cell>
                                                    <span className="flex items-center gap-2">
                                                        <span className="shrink-0 text-xs tabular-nums text-muted">
                                                            {row.code}
                                                        </span>
                                                        <span className="text-foreground">
                                                            {row.label}
                                                        </span>
                                                        <VatDrill
                                                            label={row.label}
                                                            onViewLedger={
                                                                onViewLedger
                                                            }
                                                            targets={
                                                                row.drillTargets
                                                            }
                                                            testId={`${testIdPrefix}-view-ledger-${row.accountId}`}
                                                        />
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <span
                                                        className={cn(
                                                            'block text-right tabular-nums',
                                                            row.isNegative
                                                                ? 'text-danger'
                                                                : 'text-foreground',
                                                        )}
                                                    >
                                                        {row.amountText}
                                                    </span>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                {/* The total is a real row, not a footer strip:
                                    it is part of the listing a screen reader
                                    walks, and it must line up with the amount
                                    column above it. */}
                                <Table.Row
                                    className="group border-t-2 border-foreground/60 bg-surface-secondary"
                                    id={`${testIdPrefix}-total-row`}
                                    key={`${testIdPrefix}-total-row`}
                                >
                                    <Table.Cell>
                                        <span className="flex items-center gap-2 font-semibold text-foreground">
                                            {t('vat.breakdown.total')}
                                            <VatDrill
                                                label={title}
                                                onViewLedger={onViewLedger}
                                                targets={totalDrillTargets}
                                                testId={`${testIdPrefix}-view-ledger-total`}
                                            />
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span
                                            className="block text-right font-bold tabular-nums text-foreground"
                                            {...getNodeTestIdProp(
                                                `${testIdPrefix}-total`,
                                            )}
                                        >
                                            {totalText === null ? (
                                                <Skeleton className="ml-auto h-4 w-24 rounded" />
                                            ) : (
                                                totalText
                                            )}
                                        </span>
                                    </Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table.Content>
                    </Table.ScrollContainer>
                </Table>
            </div>
        </div>
    );
};

type VatDrillProps = {
    targets: VatDrillTarget[];
    /** What the affordance is drilling *from*, for its accessible name. */
    label: string;
    onViewLedger: (accountId: string) => void;
    testId: string;
};

/**
 * The drill affordance, or nothing at all — the same three cases the statements
 * settled on, driven entirely by the composition the server sent:
 *
 * - **one account** — a direct "View in ledger" link;
 * - **several accounts** — the same affordance as a menu of them, because the
 *   journal filters by a single account at a time and the reader should be the
 *   one to choose. This is where a side total lands, and output VAT can easily
 *   span more than one account;
 * - **no accounts** — `null`. An account the server reported with no
 *   contributing entries lands here, and so does a side that had no movement at
 *   all. A control that cannot act is precisely the dead link this integration
 *   was meant to fix; shipping a second variety of it would be worse.
 */
const VatDrill: React.FC<VatDrillProps> = ({
    targets,
    label,
    onViewLedger,
    testId,
}) => {
    const { t } = useTranslation('pages');
    const first = targets[0];

    if (!first) {
        return null;
    }

    if (targets.length === 1) {
        return (
            <button
                aria-label={t('statements.viewLedgerFor', { account: label })}
                className={DRILL_CLASS_NAME}
                onClick={() => onViewLedger(first.accountId)}
                type="button"
                {...getNodeTestIdProp(testId)}
            >
                {t('statements.viewLedger')}
                <Icon aria-hidden icon="gravity-ui:arrow-right" />
            </button>
        );
    }

    return (
        <Dropdown>
            <Button
                aria-label={t('statements.viewLedgerFor', { account: label })}
                className={DRILL_CLASS_NAME}
                variant="ghost"
                {...getNodeTestIdProp(testId)}
            >
                {t('statements.viewLedger')}
            </Button>
            <Dropdown.Popover>
                <Dropdown.Menu
                    onAction={(key) => onViewLedger(String(key))}
                    {...getNodeTestIdProp(`${testId}-menu`)}
                >
                    {targets.map((target) => (
                        <Dropdown.Item
                            id={target.accountId}
                            key={target.accountId}
                            textValue={`${target.accountCode} ${target.accountName}`}
                        >
                            <div className="flex flex-col">
                                <Label>{target.accountName}</Label>
                                <Description>{target.accountCode}</Description>
                            </div>
                            <span className="ms-auto ps-4 text-xs tabular-nums text-muted">
                                {target.amountText}
                            </span>
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown>
    );
};
